import React, { useMemo } from 'react';
import { Card, Col, Empty, Row, Statistic, Table, Tag, Typography } from 'antd';
import {
  ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';

// Erlang Stats > Trend: the search's dates one row each (GET
// /api/erlang-stats/by-day), each beside the same weekday 52 weeks earlier
// (last_year, matched on MatchDay). With a Day of Week filter of Monday it is
// every Monday in the range: is it going up or down, and how does it compare
// with last year's Mondays.

const THIS = '#1677ff';
const LAST = '#bfbfbf';

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const int = (v) => (v == null ? '—' : Math.round(Number(v)).toLocaleString());
const mmss = (secs) => {
  if (secs == null) return '—';
  const t = Math.round(Number(secs));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};
const delta = (cur, prev) => (cur == null || prev == null || !Number(prev) ? null
  : ((Number(cur) - Number(prev)) / Number(prev)) * 100);

// Least-squares slope of y over the dates, per 7 days (null under 3 points).
function weeklySlope(rows, key) {
  const pts = rows.filter((r) => r[key] != null).map((r) => [dayjs(r.Day).valueOf() / 86400000, Number(r[key])]);
  if (pts.length < 3) return null;
  const mx = pts.reduce((t, [x]) => t + x, 0) / pts.length;
  const my = pts.reduce((t, [, y]) => t + y, 0) / pts.length;
  const sxx = pts.reduce((t, [x]) => t + (x - mx) ** 2, 0);
  if (!sxx) return null;
  const sxy = pts.reduce((t, [x, y]) => t + (x - mx) * (y - my), 0);
  return { perWeek: (sxy / sxx) * 7, at: (x) => my + (sxy / sxx) * (x - mx) };
}

const sumOf = (rows, k) => rows.reduce((t, r) => t + (Number(r[k]) || 0), 0);
const avgOf = (rows, k) => {
  const v = rows.filter((r) => r[k] != null);
  return v.length ? v.reduce((t, r) => t + Number(r[k]), 0) / v.length : null;
};

const signed = (v, digits, unit) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(digits)}${unit}`);
const trendColor = (v, goodUp = true) => (v == null || Math.abs(v) < 1e-9 ? undefined
  : (v > 0) === goodUp ? '#389e0d' : '#cf1322');

export default function ErlangTrend({ data, loading }) {
  const rows = useMemo(() => {
    const ly = {};
    for (const r of data?.last_year || []) ly[r.MatchDay] = r;
    return (data?.current || []).map((r) => ({ ...r, LY: ly[r.Day] || null }));
  }, [data]);

  if (!rows.length) {
    return <Card loading={loading}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick a campaign and a date range, then click Search." /></Card>;
  }

  const lyRows = rows.map((r) => r.LY).filter(Boolean);
  const offeredTrend = weeklySlope(rows, 'Offered');
  const slaTrend = weeklySlope(rows, 'RawSLA');
  const avgOffered = sumOf(rows, 'Offered') / rows.length;
  const lyAvgOffered = lyRows.length ? sumOf(lyRows, 'Offered') / lyRows.length : null;
  const avgSla = avgOf(rows, 'RawSLA');
  const lyAvgSla = avgOf(lyRows, 'RawSLA');
  const target = Math.max(0, ...rows.map((r) => Number(r.TargetSLA) || 0)) * 100 || null;

  const chart = rows.map((r) => ({
    Day: r.Day,
    Offered: r.Offered,
    LYOffered: r.LY?.Offered ?? null,
    Fit: offeredTrend ? Math.round(offeredTrend.at(dayjs(r.Day).valueOf() / 86400000) * 10) / 10 : null,
    RawSLA: r.RawSLA != null ? Number(r.RawSLA) : null,
    LYRawSLA: r.LY?.RawSLA != null ? Number(r.LY.RawSLA) : null,
    LYDay: r.LY?.Day,
  }));
  const tick = (v) => dayjs(v).format('MM/DD');
  const label = (v) => dayjs(v).format('ddd MMM D, YYYY');

  const cols = [
    { title: 'Date', dataIndex: 'Day', key: 'Day', width: 150, fixed: 'left',
      render: (v, r) => (<span>{label(v)}{r.IsHoliday ? <Tag color="gold" style={{ marginLeft: 6 }}>Holiday</Tag> : null}</span>) },
    { title: 'Offered', dataIndex: 'Offered', key: 'Offered', width: 90, align: 'right', render: int },
    { title: 'Last Year', key: 'LYOffered', width: 90, align: 'right', render: (_, r) => int(r.LY?.Offered) },
    { title: 'vs LY', key: 'OfferedDelta', width: 80, align: 'right', render: (_, r) => {
      const d = delta(r.Offered, r.LY?.Offered);
      return <span style={{ color: trendColor(d) }}>{signed(d, 1, '%')}</span>;
    } },
    { title: 'Raw SLA', dataIndex: 'RawSLA', key: 'RawSLA', width: 85, align: 'right', render: pct },
    { title: 'Last Year', key: 'LYSLA', width: 90, align: 'right', render: (_, r) => pct(r.LY?.RawSLA) },
    { title: 'Abandon %', dataIndex: 'AbandonPct', key: 'AbandonPct', width: 95, align: 'right', render: pct },
    { title: 'Last Year', key: 'LYAbandon', width: 90, align: 'right', render: (_, r) => pct(r.LY?.AbandonPct) },
    { title: 'ASA', dataIndex: 'RawASA', key: 'RawASA', width: 70, align: 'right', render: mmss },
    { title: 'AHT', dataIndex: 'RawAHT', key: 'RawAHT', width: 70, align: 'right', render: (v) => mmss(v != null ? Number(v) * 60 : null) },
    { title: 'Avg Staffed', dataIndex: 'AvgStaffed', key: 'AvgStaffed', width: 95, align: 'right',
      render: (v) => (v == null ? '—' : Number(v).toFixed(1)) },
    { title: 'Last Year Date', key: 'LYDay', width: 130, render: (_, r) => (r.LY ? dayjs(r.LY.Day).format('MMM D, YYYY') : '—') },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Avg Offered per Day" value={Math.round(avgOffered)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small">
          <Statistic title="Offered Trend (per week)" value={signed(offeredTrend?.perWeek ?? null, 1, '')}
            valueStyle={{ color: trendColor(offeredTrend?.perWeek) }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last year avg {lyAvgOffered != null ? Math.round(lyAvgOffered).toLocaleString() : '—'}
            {' '}({signed(delta(avgOffered, lyAvgOffered), 1, '%')})
          </Typography.Text>
        </Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Avg Raw SLA" value={pct(avgSla)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small">
          <Statistic title="SLA Trend (pts per week)" value={signed(slaTrend?.perWeek ?? null, 1, '')}
            valueStyle={{ color: trendColor(slaTrend?.perWeek) }} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last year avg {pct(lyAvgSla)}
          </Typography.Text>
        </Card></Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={12}>
          <Card size="small" title="Offered by Date">
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chart} margin={{ top: 10, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="Day" tickFormatter={tick} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip labelFormatter={label}
                  formatter={(v, name, { payload }) => [int(v), name === 'Last year' && payload.LYDay ? `Last year (${tick(payload.LYDay)})` : name]} />
                <Legend />
                <Bar dataKey="LYOffered" name="Last year" fill={LAST} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Offered" name="This period" fill={THIS} radius={[4, 4, 0, 0]} />
                {offeredTrend && <Line dataKey="Fit" name="Trend" stroke="#fa8c16" strokeWidth={2} dot={false} strokeDasharray="6 4" />}
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card size="small" title="Raw SLA by Date">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chart} margin={{ top: 10, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="Day" tickFormatter={tick} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                <Tooltip labelFormatter={label}
                  formatter={(v, name, { payload }) => [pct(v), name === 'Last year' && payload.LYDay ? `Last year (${tick(payload.LYDay)})` : name]} />
                <Legend />
                {target && <ReferenceLine y={target} stroke="#389e0d" strokeDasharray="4 4" label={{ value: `Target ${Math.round(target)}%`, fontSize: 10, position: 'insideTopRight' }} />}
                <Line dataKey="LYRawSLA" name="Last year" stroke={LAST} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line dataKey="RawSLA" name="This period" stroke={THIS} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Table size="small" rowKey="Day" dataSource={rows} columns={cols} loading={loading}
        pagination={false} sticky scroll={{ x: 'max-content' }} bordered />
    </div>
  );
}
